'use client'

import { useQuery } from '@tanstack/react-query'

import { chartBarStartMs } from '@/lib/chart-timeframe'
import { fetchOrderbookHeatmap } from '@/service/orderbook-heatmap.api'
import { Exchange } from '@/types/generated/common/types'
import type { OrderbookHeatmapRequest } from '@/types/generated/public_api'

const orderbookHeatmapKeys = {
  all: ['orderbookHeatmap'] as const,
  window: (params: OrderbookHeatmapRequest) => [...orderbookHeatmapKeys.all, params] as const,
}

/** Whether the window runs into the bar still open (toMs is snapped to a bar start). */
export function reachesLiveEdge(params: OrderbookHeatmapRequest) {
  return Number(params.toMs) >= chartBarStartMs(Date.now(), params.timeframe)
}

function sameSeries(a: OrderbookHeatmapRequest, b: OrderbookHeatmapRequest) {
  return a.exchange === b.exchange && a.symbol === b.symbol && a.timeframe === b.timeframe
}

/**
 * Heatmap columns for one chart window. The newest column is its bucket's opening snapshot, so a
 * window reaching the live edge polls once a bar (at most every five minutes); one wholly in the
 * past can't change and never refetches. The previous window stays on screen while a pan fetches
 * the next one — but never another series' window — and a window panned away from is dropped
 * quickly, since one response can run to megabytes.
 */
export function useOrderbookHeatmap(params: OrderbookHeatmapRequest | null) {
  return useQuery({
    queryKey: params ? orderbookHeatmapKeys.window(params) : orderbookHeatmapKeys.all,
    queryFn: ({ signal }) => fetchOrderbookHeatmap(params as OrderbookHeatmapRequest, signal),
    enabled:
      params !== null &&
      params.exchange !== Exchange.EXCHANGE_UNSPECIFIED &&
      Boolean(params.symbol),
    placeholderData: (previous, previousQuery) => {
      const previousParams = previousQuery?.queryKey[1] as OrderbookHeatmapRequest | undefined
      return params && previousParams && sameSeries(params, previousParams) ? previous : undefined
    },
    refetchInterval: () =>
      params && reachesLiveEdge(params) ? Math.min(params.timeframe, 300) * 1000 : false,
    staleTime: params && !reachesLiveEdge(params) ? Infinity : 30 * 1000,
    gcTime: 30 * 1000,
  })
}
